<template>
  <div class="container-fluid">
    <div class="d-sm-flex align-items-center justify-content-between mb-4">
      <h1 class="h3 mb-0 text-gray-800">Distribution Groups</h1>
    </div>

    <div class="row">
      <div class="col-sm-6 col-xl-3">
        <div class="p-3 card border-left-primary mb-g">
          <div>
            <small class="text-muted fs-sm">Active Groups</small>
            <h3 class="d-block l-h-n m-0 fw-700 text-primary">{{ activeGroups }}</h3>
          </div>
        </div>
      </div>
      <div class="col-sm-6 col-xl-3">
        <div class="p-3 card border-left-info mb-g">
          <div>
            <small class="text-muted fs-sm">Total Agents</small>
            <h3 class="d-block l-h-n m-0 fw-700 text-info">{{ totalAgents }}</h3>
          </div>
        </div>
      </div>
      <div class="col-sm-6 col-xl-3">
        <div class="p-3 card border-left-success mb-g">
          <div>
            <small class="text-muted fs-sm">Active DDIs</small>
            <h3 class="d-block l-h-n m-0 fw-700 text-success">{{ totalDDIs }}</h3>
          </div>
        </div>
      </div>
      <div class="col-sm-6 col-xl-3">
        <div class="p-3 card border-left-warning mb-g">
          <div>
            <small class="text-muted fs-sm">Languages</small>
            <h3 class="d-block l-h-n m-0 fw-700 text-warning">{{ languageCount }}</h3>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12 mt-3">
        <div class="card mb-4">
          <div class="card-header py-3">
            <h6 class="m-0 font-weight-bold text-primary">
              <svg xmlns="http://www.w3.org/2000/svg" style="vertical-align: sub;" width="24" height="24"
                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round" class="lucide lucide-filter w-5 h-5 text-primary">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
              </svg>
              Search &amp; Filter
            </h6>
          </div>
          <div class="card-body">
            <form @submit.prevent>
              <div class="row">
                <div class="col-md-6">
                  <div class="input-group bg-white shadow-inset-2">
                    <div class="input-group-prepend">
                      <span class="input-group-text bg-transparent border-right-0">
                        <font-awesome-icon :icon="['fas', 'search']" />
                      </span>
                    </div>
                    <input v-model.trim="searchQuery" type="text" class="form-control border-left-0 bg-transparent pl-0"
                      placeholder="Search by ID, name or email...">
                  </div>
                </div>

                <div class="col-md-3">
                  <select class="custom-select w-100" v-model="methodFilter">
                    <option value="">All Methods</option>
                    <option v-for="option in distributionOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <select class="custom-select w-100" v-model="languageFilter">
                    <option value="">All Languages</option>
                    <option v-for="lang in languageOptions" :key="lang.value" :value="lang.value">{{ lang.label }}</option>
                  </select>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-md-12 d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <div class="form-inline mb-3">
            <div class="form-group">
              <label class="pr-2">Results per page: </label>
              <select class="custom-select" v-model.number="perPage">
                <option :value="5">5</option>
                <option :value="10">10</option>
                <option :value="15">15</option>
                <option :value="0">All</option>
              </select>
            </div>
          </div>
          <p class="text-primary ml-3">Showing <strong>{{ showingCount }}</strong> of <strong>{{ filteredCount }}</strong> groups</p>
        </div>

        <button class="btn btn-primary" type="button" @click="openCreateGroupModal"><font-awesome-icon
            :icon="['fas', 'plus']" /> New Group</button>
      </div>

      <div class="col-md-12" v-if="errorMessage">
        <div class="alert alert-danger">{{ errorMessage }}</div>
      </div>

      <div class="col-md-12 table-responsive">
        <table class="table table-bordered table-striped table-hover">
          <thead>
            <tr>
              <th scope="col">ID</th>
              <th scope="col">Name</th>
              <th scope="col">Distribution</th>
              <th scope="col">Notification Email</th>
              <th scope="col">On Busy</th>
              <th scope="col">Audio</th>
              <th scope="col">Language</th>
              <th class="text-right" scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="isLoading">
              <td colspan="8" class="text-center text-muted py-4">Loading groups...</td>
            </tr>
            <tr v-else-if="!pagedGroups.length">
              <td colspan="8" class="text-center text-muted py-4">No groups found.</td>
            </tr>
            <tr v-else v-for="group in pagedGroups" :key="group.id" :class="{ 'table-secondary text-muted': group.disabled }">
              <td><a href="#" class="font-weight-bold" @click.prevent="openGroupDetails(group)">{{ group.identifier || formatGroupIdentifier(group.id) }}</a></td>
              <td>
                {{ group.name }}
                <span class="text-muted"><font-awesome-icon :icon="['fas', 'users']" /> {{ memberCount(group) }}</span>
              </td>
              <td><span class="badge border border-primary text-primary badge-pill">{{ formatAlgorithm(group.algorithm) }}</span></td>
              <td>{{ group.email || '—' }}</td>
              <td>{{ formatBusyAction(group.action_when_busy) }}</td>
              <td><span class="text-muted"><font-awesome-icon :icon="group.action === 'play' ? ['fas', 'link'] : ['fas', 'volume-up']" /> {{ group.action === 'play' ? 'URL' : 'TTS' }}</span></td>
              <td><span class="badge border border-secondary text-secondary badge-pill">{{ formatLanguage(group.lang) }}</span></td>
              <td class="text-right">
                <a href="#" :class="['btn', 'btn-icon', 'btn-sm', 'btn-action', groupActionClass(group)]" @click.prevent="openGroupStatusModal(group)">
                  <font-awesome-icon :icon="groupActionIcon(group)" />
                </a>
              </td>
            </tr>
          </tbody>
        </table>
        <nav aria-label="pagination" v-if="hasPagination">
          <ul class="pagination justify-content-end">
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <a class="page-link" href="#" @click.prevent="goToPage(currentPage - 1)">Previous</a>
            </li>
            <li class="page-item" v-for="page in paginationPages" :key="`groups-page-${page}`" :class="{ active: currentPage === page }">
              <a class="page-link" href="#" @click.prevent="goToPage(page)">{{ page }}</a>
            </li>
            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
              <a class="page-link" href="#" @click.prevent="goToPage(currentPage + 1)">Next</a>
            </li>
          </ul>
        </nav>
      </div>
    </div>


    <div :class="['modal', 'fade', 'show']" :style="{ display: showModalDetails ? 'block' : 'none' }" id="modalDetailsGroup" tabindex="-1"
      role="dialog" aria-labelledby="modalDetailsGroup" aria-hidden="true" data-backdrop="static" data-keyboard="false" v-if="showModalDetails">
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg" role="document">
        <div class="modal-content" v-if="selectedGroup">
          <div class="modal-header d-block">
            <p class="h5 modal-title" id="exampleModalLongTitle">{{ selectedGroup.name }} - {{ selectedGroup.identifier || formatGroupIdentifier(selectedGroup.id) }}</p>
            <small>Updated {{ formatTimestamp(selectedGroup.updated_at) }}</small>
            <button
              type="button"
              class="close"
              data-dismiss="modal"
              aria-label="Close"
              :disabled="isDetailsSetupLocked"
              :title="isDetailsSetupLocked ? 'Add at least 1 agent or 1 DDI to close' : ''"
              @click="hideModal('details')"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div v-if="detailsBlockError" class="alert alert-danger">
              {{ detailsBlockError }}
            </div>
            <ul class="list-group list-group-flush">
              <div class="d-flex aling-items-center">
                <li class="list-group-item border-0 col-6"><label :style="{ width: '120px' }">Distribution:
                  </label><strong>{{ formatAlgorithm(selectedGroup.algorithm) }}</strong></li>
                <li class="list-group-item border-0 col-6"><label :style="{ width: '120px' }">On Busy:
                  </label><strong>{{ formatBusyAction(selectedGroup.action_when_busy) }}</strong></li>
              </div>

              <div class="d-flex aling-items-center">
                <li class="list-group-item border-0 col-6"><label :style="{ width: '120px' }">Language:
                  </label><strong>{{ formatLanguage(selectedGroup.lang) }}</strong></li>
                <li class="list-group-item border-0 col-6"><label :style="{ width: '120px' }">Audio Type:
                  </label><strong>{{ selectedGroup.action === 'play' ? 'Audio URL' : 'Text-to-Speech' }}</strong></li>
              </div>
            </ul>

            <ul class="list-group list-group-flush">
              <li class="list-group-item border-top"><label class="d-block">Notification Email:
                </label><strong>{{ selectedGroup.email || '—' }}</strong></li>

              <li class="list-group-item"><label class="d-block"><font-awesome-icon :icon="['fas', 'volume-up']" /> Text
                  to Play:
                </label><strong>{{ selectedGroup.action_arg || '—' }}</strong></li>
            </ul>

            <hr>

            <div class="col-12">
              <ul class="nav nav-pills nav-justified mt-4" role="tablist">
                <li class="nav-item"><a class="nav-link" :class="{ active: detailTab === 'agents' }" href="#" @click.prevent="detailTab = 'agents'"><font-awesome-icon
                      :icon="['fas', 'users']" /> Agents ({{ selectedMembers.length }})</a></li>
                <li class="nav-item"><a class="nav-link" :class="{ active: detailTab === 'ddis' }" href="#" @click.prevent="detailTab = 'ddis'"><font-awesome-icon
                      :icon="['fas', 'phone-alt']" /> DDIs ({{ selectedDDIs.length }})</a></li>
              </ul>
              <div class="tab-content py-3">
                <div class="tab-pane fade" :class="{ 'active show': detailTab === 'agents' }" id="tableAgents" role="tabpanel">

                  <div class="text-right">
                    <button class="btn btn-primary my-3" @click="openAddAgentModal"
                      :disabled="selectedGroup.disabled || !availableAgents.length"><font-awesome-icon :icon="['fas', 'plus']" /> Add Agent</button>
                  </div>

                  <div class="table-responsive">
                    <table class="table table-bordered table-striped table-hover">
                      <thead>
                        <tr>
                          <th scope="col">Full Name</th>
                          <th scope="col">Email</th>
                          <th scope="col">Extension</th>
                          <th class="text-right" scope="col">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="!selectedMembers.length">
                          <td colspan="4" class="text-center text-muted">No agents assigned.</td>
                        </tr>
                        <tr v-for="member in selectedMembers" :key="member.id">
                          <td><strong>{{ member.name }}</strong></td>
                          <td>{{ member.email || '—' }}</td>
                          <td><span class="text-primary">{{ member.extension }}</span></td>
                          <td class="text-right">
                            <a href="#" :class="['btn', 'btn-outline-danger', 'btn-icon', 'btn-sm', 'btn-action', 'border-0', { disabled: selectedGroup.disabled || !canRemoveAnyAgent }]"
                              :aria-disabled="selectedGroup.disabled || !canRemoveAnyAgent ? 'true' : 'false'"
                              @click.prevent="removeAgent(member.id)">
                              <font-awesome-icon :icon="['fas', 'trash-alt']" />
                            </a>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div class="tab-pane fade" :class="{ 'active show': detailTab === 'ddis' }" id="tableDDIs" role="tabpanel">
                  <div class="text-right">
                    <button class="btn btn-primary my-3" @click="openAddDDIModal"
                      :disabled="selectedGroup.disabled || !availableDDIs.length"><font-awesome-icon :icon="['fas', 'plus']" /> Add DDI</button>
                  </div>

                  <div class="table-responsive">
                    <table class="table table-bordered table-striped table-hover">
                      <thead>
                        <tr>
                          <th scope="col">DDI Number</th>
                          <th scope="col">Direction</th>
                          <th scope="col">Supplier</th>
                          <th class="text-right" scope="col">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="!selectedDDIs.length">
                          <td colspan="4" class="text-center text-muted">No DDIs assigned.</td>
                        </tr>
                        <tr v-for="ddi in selectedDDIs" :key="ddi.id">
                          <td>{{ ddi.name }}</td>
                          <td>
                            <span :class="directionMeta(ddi.direction).class">
                              <font-awesome-icon :icon="directionMeta(ddi.direction).icon" />
                              {{ directionMeta(ddi.direction).label }}
                            </span>
                          </td>
                          <td>{{ ddi.description || '—' }}</td>
                          <td class="text-right">
                            <a href="#" :class="['btn', 'btn-outline-danger', 'btn-icon', 'btn-sm', 'btn-action', 'border-0', { disabled: selectedGroup.disabled || !canRemoveAnyDDI }]"
                              :aria-disabled="selectedGroup.disabled || !canRemoveAnyDDI ? 'true' : 'false'"
                              @click.prevent="removeDDI(ddi.id)">
                              <font-awesome-icon :icon="['fas', 'trash-alt']" />
                            </a>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>

    <div :class="['modal', 'fade', 'show']" :style="{ display: showModalAddGroup ? 'block' : 'none' }" id="modalAddGroup" tabindex="-1" role="dialog"
      aria-labelledby="modalAddGroup" aria-hidden="true" data-backdrop="static" data-keyboard="false" v-if="showModalAddGroup">
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header d-block">
            <p class="h5 modal-title" id="exampleModalLongTitle">Create New Distribution Group</p>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close" @click="hideModal('addGroup')">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div v-if="addGroupError" class="alert alert-danger">
              {{ addGroupError }}
            </div>
            <ul class="list-group list-group-flush">
              <li class="list-group-item pt-0"><label>Group Name*
                </label>
                <input v-model.trim="groupForm.name" type="text" class="form-control" required>
              </li>
              <div class="d-flex">
                <li class="list-group-item col-6 border-top-0 border-left-0 border-right-0"><label>Distribution
                    Method
                  </label>
                  <select class="custom-select w-100" v-model="groupForm.algorithm">
                    <option v-for="option in distributionOptions" :key="option.value" :value="option.value" :disabled="option.disabled">{{ option.label }}</option>
                  </select>
                </li>

                <li class="list-group-item col-6 border-top-0 border-left-0 border-right-0"><label>
                    Language
                  </label>
                  <select class="custom-select w-100" v-model="groupForm.lang">
                    <option v-for="lang in languageOptions" :key="lang.value" :value="lang.value">{{ lang.label }}</option>
                  </select>
                </li>
              </div>

              <li class="list-group-item"><label>Notification Email*
                </label>
                <input v-model.trim="groupForm.email" type="email" class="form-control" required>
              </li>

              <li class="list-group-item"><label>
                  Action When Busy
                </label>
                <select class="custom-select w-100" v-model="groupForm.actionWhenBusy">
                  <option v-for="option in busyActionOptions" :key="option.value" :value="option.value" :disabled="option.disabled">{{ option.label }}</option>
                </select>
              </li>

              <li class="list-group-item">
                <p>Audio Configuration*</p>
                <ul class="nav nav-pills mb-3" id="pills-tab" role="tablist">
                  <li class="nav-item" role="presentation">
                    <button class="border-0 bg-white pb-0" :class="{ active: groupForm.action === 'say' }" id="pills-text-tab" data-toggle="pill"
                      data-target="#pills-text" type="button" role="tab" aria-controls="pills-text"
                      aria-selected="true" @click="groupForm.action = 'say'">
                      <div class="custom-control custom-radio custom-control-inline">
                        <input type="radio" id="TTSId" name="customRadioInline" class="custom-control-input" :checked="groupForm.action === 'say'">
                        <label class="custom-control-label" for="TTSId">Text-to-Speech</label>
                      </div>
                    </button>
                  </li>
                  <li class="nav-item" role="presentation">
                    <button class="border-0 bg-white pb-0" :class="{ active: groupForm.action === 'play' }" id="pills-url-tab" data-toggle="pill"
                      data-target="#pills-url" type="button" role="tab" aria-controls="pills-url" aria-selected="false" @click="groupForm.action = 'play'">
                      <div class="custom-control custom-radio custom-control-inline">
                        <input type="radio" id="AudioId" name="customRadioInline" class="custom-control-input" :checked="groupForm.action === 'play'">
                        <label class="custom-control-label" for="AudioId">Audio URL</label>
                      </div>
                    </button>
                  </li>
                </ul>
                <div class="tab-content" id="pills-tabContent">
                  <div class="tab-pane fade pb-3" :class="{ 'show active': groupForm.action === 'say' }" id="pills-text" role="tabpanel"
                    aria-labelledby="pills-text-tab">
                    <textarea v-model.trim="groupForm.actionArg" class="form-control" id="textToSpeech" rows="5"
                      placeholder="Enter the text to be spoken..."></textarea>
                  </div>
                  <div class="tab-pane fade pb-3" :class="{ 'show active': groupForm.action === 'play' }" id="pills-url" role="tabpanel" aria-labelledby="pills-url-tab">
                    <input v-model.trim="groupForm.actionArg" type="text" class="form-control" id="urlAudio"
                      placeholder="https://example.com/audio.mp3">
                  </div>
                </div>
              </li>
            </ul>

            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-dismiss="modal" @click="hideModal('addGroup')">Cancel</button>
              <button type="button" class="btn btn-primary" :disabled="isSaving" @click="createGroup">{{ isSaving ? 'Creating...' : 'Create Group' }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div :class="['modal', 'fade', 'show']" :style="{ display: showModalAddAgent ? 'block' : 'none' }" id="modalAddAgent" tabindex="-1" role="dialog"
      aria-labelledby="modalAddAgent" aria-hidden="true" data-backdrop="static" data-keyboard="false" v-if="showModalAddAgent">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header d-block">
            <p class="h5 modal-title" id="exampleModalLongTitle">Add Agent</p>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close" @click="hideModal('addAgent')">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div v-if="addAgentError" class="alert alert-danger">
              {{ addAgentError }}
            </div>
            <ul class="list-group list-group-flush">
              <li class="list-group-item pt-0"><label>Full Name*
                </label>
                <SearchableSelect
                  :options="availableAgents"
                  label-key="name"
                  sub-label-key="email"
                  placeholder="John Doe"
                  no-results-text="No agents found"
                  :value="selectedAgentId"
                  @input="selectedAgentId = $event"
                />
              </li>

              <li class="list-group-item"><label>Email*
                </label>
                <input type="email" class="form-control" :value="selectedAgentMeta.email || ''" placeholder="john.doe@company.com" readonly>
              </li>

              <li class="list-group-item"><label>Extension
                </label>
                <input type="text" class="form-control" :value="selectedAgentMeta.extension || ''" placeholder="1001" readonly>
              </li>
            </ul>

            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-dismiss="modal" @click="hideModal('addAgent')">Cancel</button>
              <button type="button" class="btn btn-primary" :disabled="!selectedAgentId || isSaving" @click="confirmAddAgent">Add Agent</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div :class="['modal', 'fade', 'show']" :style="{ display: showModalAddDDI ? 'block' : 'none' }" id="modalAddDDI" tabindex="-1" role="dialog"
      aria-labelledby="modalAddDDI" aria-hidden="true" data-backdrop="static" data-keyboard="false" v-if="showModalAddDDI">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header d-block">
            <p class="h5 modal-title" id="exampleModalLongTitle">Add DDI</p>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close" @click="hideModal('addDDI')">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div v-if="addDDIError" class="alert alert-danger">
              {{ addDDIError }}
            </div>
            <ul class="list-group list-group-flush">
              <li class="list-group-item pt-0"><label>DDI Number*
                </label>
                <SearchableSelect
                  :options="availableDDIs"
                  label-key="name"
                  sub-label-key="description"
                  placeholder="+1-555-0100"
                  no-results-text="No DDIs found"
                  :value="selectedDDIId"
                  @input="selectedDDIId = $event"
                />
              </li>

              <li class="list-group-item">
                <label>Direction*</label>
                <select class="custom-select w-100" disabled>
                  <option value="in">Inbound</option>
                </select>
              </li>

              <li class="list-group-item"><label>Supplier
                </label>
                <input type="text" class="form-control" :value="selectedDDIMeta.description || ''" placeholder="Main support line" readonly>
              </li>
            </ul>

            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-dismiss="modal" @click="hideModal('addDDI')">Cancel</button>
              <button type="button" class="btn btn-primary" :disabled="!selectedDDIId || isSaving" @click="confirmAddDDI">Add DDI</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div :class="['modal', 'fade', 'show']" :style="{ display: showModalGroupConfirm ? 'block' : 'none' }" id="modalGroupStatus" tabindex="-1"
      role="dialog" aria-labelledby="modalGroupStatus" aria-hidden="true" data-backdrop="static"
      data-keyboard="false" v-if="showModalGroupConfirm">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header d-block">
            <p class="h5 modal-title" id="exampleModalLongTitle">{{ pendingGroupAction === 'enable' ? 'Enable Group' : 'Disable Group' }}</p>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close" @click="hideGroupStatusModal">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <p v-if="pendingGroupAction === 'disable'">Are you sure you want to disable <strong>{{ pendingGroupStatus ? pendingGroupStatus.name : '' }}</strong>?</p>
            <p v-else>Are you sure you want to enable <strong>{{ pendingGroupStatus ? pendingGroupStatus.name : '' }}</strong>?</p>

            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-dismiss="modal" @click="hideGroupStatusModal">Cancel</button>
              <button type="button" :class="['btn', pendingGroupAction === 'enable' ? 'btn-success' : 'btn-danger']" :disabled="isUpdatingGroupStatus" @click="confirmGroupStatusAction">{{ isUpdatingGroupStatus ? (pendingGroupAction === 'enable' ? 'Enabling...' : 'Disabling...') : (pendingGroupAction === 'enable' ? 'Enable' : 'Disable') }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from '@/services/api.js';
import SearchableSelect from '@/components/SearchableSelect.vue';

const DISTRIBUTION_OPTIONS = [
  { value: 'load', label: 'Round Robin', disabled: true },
  { value: 'last', label: 'Longest Idle', disabled: true },
  { value: 'all', label: 'Ring All', disabled: false },
  { value: 'linear', label: 'Linear', disabled: true },
  { value: 'random', label: 'Random', disabled: true },
];

const BUSY_OPTIONS = [
  { value: 'voicemail', label: 'Voicemail', disabled: false },
  { value: 'queue', label: 'Queue', disabled: true },
  { value: 'forward', label: 'Forward', disabled: true },
  { value: 'hangup', label: 'Hangup', disabled: true },
  { value: 'callback', label: 'Callback', disabled: true },
];

const LANGUAGE_OPTIONS = [
  { value: 'en-GB', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'it', label: 'Italian' },
];

const emptyGroupForm = () => ({
  name: '',
  algorithm: 'all',
  email: '',
  lang: 'en-GB',
  actionWhenBusy: 'voicemail',
  action: 'say',
  actionArg: '',
});

export default {
  name: 'Groups',
  components: {
    SearchableSelect,
  },
  data() {
    return {
      groups: [],
      agents: [],
      ddis: [],
      isLoading: false,
      isSaving: false,
      errorMessage: null,
      searchQuery: '',
      methodFilter: '',
      languageFilter: '',
      perPage: 5,
      currentPage: 1,
      detailTab: 'agents',
      showModalDetails: false,
      showModalAddGroup: false,
      showModalAddAgent: false,
      showModalAddDDI: false,
      showModalGroupConfirm: false,
      forceSetupAfterCreate: false,
      detailsBlockError: null,
      pendingGroupStatus: null,
      pendingGroupAction: 'disable',
      isUpdatingGroupStatus: false,
      selectedGroupId: null,
      selectedAgentId: null,
      selectedDDIId: null,
      addGroupError: null,
      addAgentError: null,
      addDDIError: null,
      groupForm: emptyGroupForm(),
      distributionOptions: DISTRIBUTION_OPTIONS,
      busyActionOptions: BUSY_OPTIONS,
      languageOptions: LANGUAGE_OPTIONS,
    };
  },
  computed: {
    totalGroups() {
      return this.groups.length;
    },
    activeGroups() {
      return (this.groups || []).filter((group) => !group.disabled).length;
    },
    totalAgents() {
      return this.agents.length;
    },
    totalDDIs() {
      return (this.ddis || []).filter((ddi) => String(ddi.status || 'active').toLowerCase() !== 'inactive').length;
    },
    languageCount() {
      return new Set((this.groups || []).map((g) => g.lang).filter(Boolean)).size;
    },
    filteredGroups() {
      const q = (this.searchQuery || '').toLowerCase();
      const filtered = (this.groups || []).filter((group) => {
        if (this.methodFilter && group.algorithm !== this.methodFilter) return false;
        if (this.languageFilter && group.lang !== this.languageFilter) return false;
        if (!q) return true;
        return (
          (group.identifier || '').toLowerCase().includes(q) ||
          (group.name || '').toLowerCase().includes(q) ||
          (group.email || '').toLowerCase().includes(q)
        );
      });
      return filtered.slice().sort((a, b) => {
        const disabledDiff = Number(!!a.disabled) - Number(!!b.disabled);
        if (disabledDiff !== 0) return disabledDiff;
        return b.id - a.id;
      });
    },
    pagedGroups() {
      if (this.perPage === 0) return this.filteredGroups;
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredGroups.slice(start, start + this.perPage);
    },
    filteredCount() {
      return this.filteredGroups.length;
    },
    showingCount() {
      return this.pagedGroups.length;
    },
    totalPages() {
      if (this.perPage === 0) return 1;
      return Math.max(1, Math.ceil(this.filteredCount / this.perPage));
    },
    hasPagination() {
      return this.totalPages > 1;
    },
    paginationPages() {
      return Array.from({ length: this.totalPages }, (_, index) => index + 1);
    },
    selectedGroup() {
      return this.groups.find((group) => group.id === this.selectedGroupId) || null;
    },
    selectedMembers() {
      return this.selectedGroup && Array.isArray(this.selectedGroup.members) ? this.selectedGroup.members : [];
    },
    selectedDDIs() {
      return this.selectedGroup && Array.isArray(this.selectedGroup.ddis) ? this.selectedGroup.ddis : [];
    },
    selectedMemberIds() {
      return this.selectedMembers.map((member) => member.id);
    },
    selectedDDIIds() {
      return this.selectedDDIs.map((ddi) => ddi.id);
    },
    availableAgents() {
      return (this.agents || [])
        .filter((agent) => !this.selectedMemberIds.includes(agent.id))
        .slice()
        .sort((a, b) => {
          const disabledDiff = Number(a.status === 'disabled') - Number(b.status === 'disabled');
          if (disabledDiff !== 0) return disabledDiff;
          return b.id - a.id;
        });
    },
    availableDDIs() {
      return (this.ddis || [])
        .filter((ddi) => !this.selectedDDIIds.includes(ddi.id))
        .slice()
        .sort((a, b) => {
          const inactiveDiff = Number(String(a.status).toLowerCase() === 'inactive') - Number(String(b.status).toLowerCase() === 'inactive');
          if (inactiveDiff !== 0) return inactiveDiff;
          return b.id - a.id;
        });
    },
    selectedAgentMeta() {
      const selectedId = this.normalizeId(this.selectedAgentId);
      if (selectedId == null) return {};
      return this.agents.find((agent) => this.normalizeId(agent.id) === selectedId) || {};
    },
    selectedDDIMeta() {
      const selectedId = this.normalizeId(this.selectedDDIId);
      if (selectedId == null) return {};
      return this.ddis.find((ddi) => this.normalizeId(ddi.id) === selectedId) || {};
    },
    isDetailsSetupLocked() {
      return this.forceSetupAfterCreate && !this.hasMinimumGroupSetup();
    },
    canRemoveAnyAgent() {
      return this.selectedMembers.length > 1 && this.selectedDDIs.length > 0;
    },
    canRemoveAnyDDI() {
      return this.selectedDDIs.length > 1 && this.selectedMembers.length > 0;
    },
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    methodFilter() {
      this.currentPage = 1;
    },
    languageFilter() {
      this.currentPage = 1;
    },
    perPage(value) {
      if (value < 0) this.perPage = 0;
      this.currentPage = 1;
    },
    filteredCount() {
      if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages;
      }
    },
  },
  created() {
    this.fetchAll();
  },
  methods: {
    fetchAll() {
      this.isLoading = true;
      Promise.all([this.fetchGroups(), this.fetchAgents(), this.fetchDDIs()]).finally(() => {
        this.isLoading = false;
      });
    },
    fetchGroups() {
      return ApiService.getGroups()
        .then((response) => {
          this.groups = response.data || [];
          if (this.selectedGroupId != null) {
            const updated = this.groups.find((group) => group.id === this.selectedGroupId);
            if (!updated) this.selectedGroupId = null;
          }
        })
        .catch((error) => {
          this.errorMessage = 'Unable to load groups.';
          throw error;
        });
    },
    refreshSelectedGroup() {
      if (this.selectedGroupId == null) return Promise.resolve();
      return ApiService.getGroup(this.selectedGroupId)
        .then((response) => {
          const group = response.data;
          if (!group) return;
          const index = this.groups.findIndex((entry) => entry.id === group.id);
          if (index === -1) {
            this.groups = [group].concat(this.groups || []);
            return;
          }
          const nextGroups = (this.groups || []).slice();
          nextGroups.splice(index, 1, group);
          this.groups = nextGroups;
        })
        .catch(() => {});
    },
    fetchAgents() {
      return ApiService.getAgents()
        .then((response) => {
          this.agents = response.data || [];
        })
        .catch((error) => {
          this.errorMessage = 'Unable to load agents.';
          throw error;
        });
    },
    fetchDDIs() {
      return ApiService.getDDIs()
        .then((response) => {
          this.ddis = response.data || [];
        })
        .catch((error) => {
          this.errorMessage = 'Unable to load DDIs.';
          throw error;
        });
    },
    showModal(modal) {
      this.showModalDetails = modal === 'details';
      this.showModalAddGroup = modal === 'addGroup';
      this.showModalAddAgent = modal === 'addAgent';
      this.showModalAddDDI = modal === 'addDDI';
    },
    hideModal(modal) {
      if (modal === 'details') {
        if (this.isDetailsSetupLocked) {
          this.detailsBlockError = 'You must add at least 1 agent and 1 DDI before closing this modal.';
          return;
        }
        this.showModalDetails = false;
        this.detailsBlockError = null;
        this.forceSetupAfterCreate = false;
      }
      if (modal === 'addGroup') this.showModalAddGroup = false;
      if (modal === 'addAgent') this.showModalAddAgent = false;
      if (modal === 'addDDI') this.showModalAddDDI = false;
      if (modal === 'addAgent') this.selectedAgentId = null;
      if (modal === 'addDDI') this.selectedDDIId = null;
      if (modal === 'addGroup') this.addGroupError = null;
      if (modal === 'addAgent') this.addAgentError = null;
      if (modal === 'addDDI') this.addDDIError = null;
    },
    openGroupDetails(group) {
      this.selectedGroupId = group.id;
      this.detailTab = 'agents';
      this.forceSetupAfterCreate = false;
      this.detailsBlockError = null;
      this.showModal('details');
      this.refreshSelectedGroup();
    },
    openCreateGroupModal() {
      this.groupForm = emptyGroupForm();
      this.errorMessage = null;
      this.addGroupError = null;
      this.showModal('addGroup');
    },
    openGroupStatusModal(group) {
      if (!group) return;
      this.pendingGroupStatus = group;
      this.pendingGroupAction = group.disabled ? 'enable' : 'disable';
      this.showModalGroupConfirm = true;
    },
    hideGroupStatusModal() {
      this.showModalGroupConfirm = false;
      this.pendingGroupStatus = null;
      this.pendingGroupAction = 'disable';
    },
    openAddAgentModal() {
      if (!this.selectedGroup || this.selectedGroup.disabled || !this.availableAgents.length) return;
      this.selectedAgentId = null;
      this.addAgentError = null;
      this.showModalAddAgent = true;
    },
    openAddDDIModal() {
      if (!this.selectedGroup || this.selectedGroup.disabled || !this.availableDDIs.length) return;
      this.selectedDDIId = null;
      this.addDDIError = null;
      this.showModalAddDDI = true;
    },
    formatGroupIdentifier(id) {
      if (id == null) return '';
      return `GRP${String(id).padStart(4, '0')}`;
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage) return;
      this.currentPage = page;
    },
    groupActionClass(group) {
      return group && group.disabled ? 'btn-outline-success' : 'btn-outline-danger';
    },
    groupActionIcon(group) {
      return group && group.disabled ? ['fas', 'check'] : ['fas', 'ban'];
    },
    normalizeId(value) {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    },
    memberCount(group) {
      return group && Array.isArray(group.members) ? group.members.length : 0;
    },
    formatAlgorithm(value) {
      const option = this.distributionOptions.find((opt) => opt.value === value);
      return option ? option.label : '—';
    },
    formatBusyAction(value) {
      const option = this.busyActionOptions.find((opt) => opt.value === value);
      return option ? option.label : '—';
    },
    formatLanguage(value) {
      const option = this.languageOptions.find((lang) => lang.value === value);
      return option ? option.label : value || '—';
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return '—';
      const date = new Date(Number(timestamp) * 1000);
      if (Number.isNaN(date.getTime())) return '—';
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const day = String(date.getDate()).padStart(2, '0');
      const month = months[date.getMonth()];
      const year = String(date.getFullYear()).slice(-2);
      return `${day}-${month}-${year}`;
    },
    extractApiError(error, fallbackMessage) {
      const data = error && error.response ? error.response.data : null;
      if (data && typeof data === 'object' && data.error) return String(data.error);
      if (typeof data === 'string') return data;
      return fallbackMessage;
    },
    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim());
    },
    directionMeta(direction) {
      if (direction === 'out') {
        return { label: 'Outbound', class: 'text-warning', icon: ['fas', 'arrow-up'] };
      }
      if (direction === 'both') {
        return { label: 'Both', class: 'text-info', icon: ['fas', 'exchange-alt'] };
      }
      return { label: 'Inbound', class: 'text-success', icon: ['fas', 'arrow-down'] };
    },
    buildUpdatePayload(memberIds, ddiIds) {
      if (!this.selectedGroup) return null;
      return {
        name: this.selectedGroup.name,
        email: this.selectedGroup.email || '',
        lang: this.selectedGroup.lang || 'en-GB',
        algorithm: this.selectedGroup.algorithm || 'all',
        action_when_busy: this.selectedGroup.action_when_busy || 'voicemail',
        action: this.selectedGroup.action || 'say',
        action_arg: this.selectedGroup.action_arg || '',
        members: memberIds,
        ddis: ddiIds,
      };
    },
    hasMinimumGroupSetup() {
      return this.selectedMemberIds.length > 0 && this.selectedDDIIds.length > 0;
    },
    createGroup() {
      this.errorMessage = null;
      this.addGroupError = null;
      this.detailsBlockError = null;
      if (!this.isValidEmail(this.groupForm.email)) {
        this.addGroupError = 'Email format is invalid.';
        return;
      }
      const payload = {
        name: this.groupForm.name.trim(),
        email: this.groupForm.email.trim(),
        lang: this.groupForm.lang,
        algorithm: this.groupForm.algorithm,
        action_when_busy: this.groupForm.actionWhenBusy,
        action: this.groupForm.action,
        action_arg: this.groupForm.actionArg.trim(),
        members: [],
        ddis: [],
      };

      const existingGroupIds = new Set((this.groups || []).map((group) => group.id));
      this.isSaving = true;
      ApiService.createGroup(payload)
        .then(() => this.fetchGroups())
        .then(() => {
          const createdGroup = this.groups.find(
            (group) => !existingGroupIds.has(group.id) && group.name === payload.name
          ) || this.groups
            .filter((group) => group.name === payload.name)
            .sort((a, b) => b.id - a.id)[0];

          this.hideModal('addGroup');
          this.groupForm = emptyGroupForm();
          if (createdGroup) {
            this.selectedGroupId = createdGroup.id;
            this.detailTab = 'agents';
            this.forceSetupAfterCreate = true;
            this.detailsBlockError = null;
            this.showModal('details');
            this.refreshSelectedGroup();
          }
        })
        .catch((error) => {
          const data = error && error.response ? error.response.data : null;
          if (data && typeof data === 'object' && data.error) {
            this.addGroupError = String(data.error);
          } else if (typeof data === 'string') {
            this.addGroupError = data;
          } else {
            this.addGroupError = 'Error creating group.';
          }
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
    confirmAddAgent() {
      if (!this.selectedGroup || this.selectedGroup.disabled || !this.selectedAgentId) return;
      this.addAgentError = null;
      this.detailsBlockError = null;
      const selectedId = this.normalizeId(this.selectedAgentId);
      if (selectedId == null) {
        this.addAgentError = 'Invalid agent selected.';
        return;
      }
      this.isSaving = true;
      ApiService.addAgentToGroup(this.selectedGroup.id, selectedId)
        .then(() => this.fetchGroups())
        .then(() => this.refreshSelectedGroup())
        .then(() => {
          this.hideModal('addAgent');
        })
        .catch((error) => {
          this.addAgentError = this.extractApiError(error, 'Error adding agent.');
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
    confirmAddDDI() {
      if (!this.selectedGroup || this.selectedGroup.disabled || !this.selectedDDIId) return;
      this.addDDIError = null;
      this.detailsBlockError = null;
      const selectedId = this.normalizeId(this.selectedDDIId);
      if (selectedId == null) {
        this.addDDIError = 'Invalid DDI selected.';
        return;
      }
      this.isSaving = true;
      ApiService.addDDIToGroup(this.selectedGroup.id, selectedId)
        .then(() => this.fetchGroups())
        .then(() => this.refreshSelectedGroup())
        .then(() => {
          this.hideModal('addDDI');
        })
        .catch((error) => {
          this.addDDIError = this.extractApiError(error, 'Error adding DDI.');
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
    removeAgent(agentId) {
      if (!this.selectedGroup || this.selectedGroup.disabled) return;
      if (!this.canRemoveAnyAgent) {
        this.detailsBlockError = 'A group must keep at least 1 agent and 1 DDI.';
        return;
      }
      this.detailsBlockError = null;
      this.isSaving = true;
      ApiService.removeAgentFromGroup(this.selectedGroup.id, agentId)
        .then(() => this.fetchGroups())
        .then(() => this.refreshSelectedGroup())
        .catch((error) => {
          const data = error && error.response ? error.response.data : null;
          const message = (data && data.error) || 'Error removing agent.';
          this.detailsBlockError = message;
          this.errorMessage = message;
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
    removeDDI(ddiId) {
      if (!this.selectedGroup || this.selectedGroup.disabled) return;
      if (!this.canRemoveAnyDDI) {
        this.detailsBlockError = 'A group must keep at least 1 agent and 1 DDI.';
        return;
      }
      this.detailsBlockError = null;
      this.isSaving = true;
      ApiService.removeDDIFromGroup(this.selectedGroup.id, ddiId)
        .then(() => this.fetchGroups())
        .then(() => this.refreshSelectedGroup())
        .catch((error) => {
          const data = error && error.response ? error.response.data : null;
          const message = (data && data.error) || 'Error removing DDI.';
          this.detailsBlockError = message;
          this.errorMessage = message;
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
    confirmGroupStatusAction() {
      if (!this.pendingGroupStatus) return;
      this.errorMessage = null;
      this.isUpdatingGroupStatus = true;
      const actionReq = this.pendingGroupAction === 'enable'
        ? ApiService.enableGroup(this.pendingGroupStatus.id)
        : ApiService.disableGroup(this.pendingGroupStatus.id);
      actionReq
        .then(() => this.fetchGroups())
        .then(() => {
          if (this.pendingGroupAction === 'disable' && this.selectedGroupId === this.pendingGroupStatus.id) {
            this.hideModal('details');
          }
          this.hideGroupStatusModal();
        })
        .catch((error) => {
          const fallback = this.pendingGroupAction === 'enable' ? 'Error enabling group.' : 'Error disabling group.';
          this.errorMessage = this.extractApiError(error, fallback);
        })
        .finally(() => {
          this.isUpdatingGroupStatus = false;
        });
    },
  },
};
</script>

<style scoped>
.mb-g {
  margin-bottom: 1rem;
}
</style>
